import { FeatureTierLevel, TierItemMap, isAdditionalUserItemId } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';

import { CartComponent } from './cart-component';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { chargebeePortalClick } from '../helpers/chargebee';
import { isDefined } from '../helpers/utils';
import { openChatWindow } from '../helpers/intercom';
import organizationInfo from '../helpers/state/organization-info';
import subscriptionInfo from '../helpers/state/subscription-info';

@customElement('subscription-cards')
export class SubscriptionsCards extends LitElement {
    amplitude_callsite: string = 'Subscription Cards';

    @query('#cart-component')
    cartComponentElement!: CartComponent;

    @state()
    changingSubscription: boolean = false;

    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindSubscriptionState = new StateController(this, subscriptionInfo);

    private goToTeamMembers() {
        this.dispatchEvent(new CustomEvent('goToTeamMembers'));
    }

    private async setToChangeSubscription() {
        if (subscriptionInfo.subscription_tier === FeatureTierLevel.Teams) {
            await chargebeePortalClick(this.amplitude_callsite);
        } else {
           this.changingSubscription = true;
        }
    };

    render() {
        if (this.changingSubscription) {
            return this.renderSubscriptionChanger();
        } else {
            return this.renderCurrentSubscription();
        }
    }

    openWorkspaceDropdown() {
        // Dispatch event to parent component to handle workspace dropdown
        this.dispatchEvent(new CustomEvent('openWorkspaceDropdown', {
            bubbles: true,
            composed: true
        }));
    }

    renderCurrentSubscription() {
        const subscription = subscriptionInfo.subscription;
        const tier = TierItemMap[subscription?.plan_id ?? ''];
        const billingFrequencyTag = subscription?.billing_period_unit === 'year' ? 'Year' : subscription?.billing_period_unit === 'month' && subscription?.billing_period === 1 ? 'Month' : '3 Months';
        const primaryItem = subscription?.subscription_items?.filter((item) => item.item_type === 'plan')[0];
        // Seats live on the plan quantity for older subscriptions and on the seat add-on for newer ones.
        const seatAddOnQuantity = subscription?.subscription_items?.filter((item) => isAdditionalUserItemId(item.item_price_id)).reduce((acc, item) => acc + item.quantity, 0) ?? 0;
        const quantity = (primaryItem?.quantity ?? 1) + seatAddOnQuantity;
        const topLevelUnit = tier === 'Teams' ? 'Team' : 'User';
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const topLevelStandardPrice = primaryItem?.unit_price ?? 1;
        // discount_amount covers the whole line (already x quantity) while unit_price is per seat.
        const topLevelWithDiscount = (primaryItem?.unit_price ?? 1) - Math.round((primaryItem?.discount_amount ?? 0) / Math.max(quantity, 1));
        // Extra seats bill as an add-on but are not a feature, so they stay in the total and out of
        // the Add Ons list.
        const featureAddOns = subscription?.subscription_items?.filter((item) => item.item_type === 'addon' && !isAdditionalUserItemId(item.item_price_id)) ?? [];
        const numAddOns = featureAddOns.length;
        const seatAddOns = subscription?.subscription_items?.filter((item) => item.item_type === 'addon' && isAdditionalUserItemId(item.item_price_id)) ?? [];
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const addOnsTotal = featureAddOns.reduce((acc, item) => acc + (item.amount - (item.discount_amount ?? 0)), 0);
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const addOnsFullTotal = featureAddOns.reduce((acc, item) => acc + item.amount, 0);
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const seatsTotal = seatAddOns.reduce((acc, item) => acc + (item.amount - (item.discount_amount ?? 0)), 0);
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const seatsFullTotal = seatAddOns.reduce((acc, item) => acc + item.amount, 0);
        // A coupon can apply to seats and add-ons alike, so both show the struck-through original.
        const lineSummary = (label: string, net: number, full: number) => net <= 0 ? '' : full > net
            ? html` + <span style="text-decoration: line-through; color: var(--main-invalid-color)">$${full/100}</span> <span style="color: var(--main-secondary-color)">$${net/100}</span> ${label}`
            : html` + $${net/100} ${label}`;
        const seatsSummary = lineSummary('Seats', seatsTotal, seatsFullTotal);
        const addOnsSummary = lineSummary('Add Ons', addOnsTotal, addOnsFullTotal);
        const priceTotal = subscription?.subscription_items?.filter((item) => item.item_type === 'addon' || item.item_type === 'plan').reduce((acc, item) => acc + (item.amount - (item.discount_amount ?? 0)), 0) ?? 0;
        const nextBilling = new Date(subscription?.next_billing_at ?? '').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const termEnd = new Date(subscription?.current_term_end ?? '').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        return html`
            <div class="flexContainer" style="flex-wrap: wrap; flex-direction: column; gap: 1rem">
                ${(subscription?.status !== 'active' &&  subscription?.status !== 'in_trial' && subscription?.status !== 'non_renewing') || !isDefined(subscription) ? html`
                    <div class="billingCard">
                        <div class="cardTitle">No Active Subscription</div>
                            <div class="cardButtons flexContainer flexEnd" style="gap: 0.75rem; margin-top: 2rem">
                            <form-button size="lg" class="button" backgroundColor="var(--main-primary-color)" @click="${this.setToChangeSubscription}">
                                Subscribe
                            </form-button>
                        </div>
                    </div>
                    <div style="text-align: center; margin-top: 1rem;">
                        <p>
                            Questions? Something not right?
                            <span @click=${() => openChatWindow(this.amplitude_callsite, 'I have questions about my charges')} style="color: var(--main-secondary-color); cursor: pointer; text-decoration: underline;">
                                Contact us!
                            </span>
                        </p>
                        <p style="margin-top: 0.5rem; color:">
                            Don't see your subscription? It might be in a different workspace.
                            <span @click=${this.openWorkspaceDropdown} style="color: var(--main-secondary-color); cursor: pointer; text-decoration: underline; font-weight: 500;">
                                Switch workspace
                            </span>
                        </p>
                    </div>
                ` : html`
                    <div class="billingCard ">
                        <div class="flexContainer justifyBetween" style="gap: 1.5rem">
                            <div>
                                <div class="cardTitle">
                                    Proxi ${tier} ${subscription?.status === 'in_trial' ? '(Trial)' : ''}
                                    ${numAddOns > 0 ? html`+ ${numAddOns} Add On${numAddOns > 1 ? 's' : ''}` : ''}
                                </div>
                                ${organizationInfo.organization?.enterprise_billing === true ? '' : html`
                                    <form-button size="sm" class="manageSubscription" @click="${this.setToChangeSubscription}" icon="edit">
                                        Update Your Account or Number of Seats
                                    </form-button>
                                `}
                            </div>
                            <div class="flexContainer alignCenter" style="flex-direction: column">
                                ${subscription?.status === 'in_trial' ? html`
                                    Trial End Date<span class="periodTag" style="color: var(--theme-secondary-color)">${nextBilling}</span>
                                ` : subscription?.status === 'active' ? html`
                                    Next Billing Date<span class="periodTag">${nextBilling}</span>
                                ` : subscription?.status === 'non_renewing' ? html`
                                    Subscription End Date<span class="periodTag" style="color: var(--theme-error-color)">${termEnd}</span>
                                ` : ''}
                            </div>
                        </div>
                        <br><br>
                        ${organizationInfo.organization?.enterprise_billing !== true ? html`
                            <div class="flexContainer justifyBetween" style="gap: 1.5rem">
                                <div>
                                    <span class="priceSection">$${priceTotal/100}</span>
                                    <br>
                                    ${topLevelWithDiscount !== topLevelStandardPrice ?
                                        html`
                                            (<span style="text-decoration: line-through; color: var(--main-invalid-color)">$${topLevelStandardPrice/100}</span>
                                            <span style="color: var(--main-secondary-color)">$${topLevelWithDiscount/100}</span>
                                            <span>${tier}${seatsSummary}${addOnsSummary}) </span>
                                            <br>
                                            per ${topLevelUnit.toLowerCase()} billed per ${billingFrequencyTag.toLowerCase()}
                                            <br>
                                            Discount Code: <span style="color: var(--main-secondary-color)">${primaryItem?.discount_name ?? 'Unknown'}</span>
                                        `
                                        : html `
                                            ($${topLevelWithDiscount/100} ${tier}${seatsSummary}${addOnsSummary})
                                            <br>
                                            per ${topLevelUnit.toLowerCase()} billed per ${billingFrequencyTag.toLowerCase()}
                                        `}
                                </div>
                                <div class="flexContainer alignRight" style="flex-direction: column">
                                    <span class="priceSection">${quantity} ${topLevelUnit}${quantity > 1 ? 's' : ''}</span>
                                    Charge ID: <br>${subscription?.subscription_id}
                                    ${organizationInfo.id ? html`<br>Workspace ID: <br>${organizationInfo.id.$oid}` : ''}
                                </div>
                            </div>
                            ${numAddOns > 0 ? html`
                                <br>
                                <br>
                                <b>Add Ons:</b>
                                ${featureAddOns.map((item) => html`
                                    <div class="flexContainer justifyBetween">
                                        <div>
                                            <span>${item.pretty_item_name ?? item.item_price_id} (${item.quantity})</b></span>
                                        </div>
                                        ${isDefined(item.discount_amount) && item.discount_amount > 0 ?
                                            html`
                                                <div>
                                                    <span style="color: var(--main-invalid-color);text-decoration: line-through">$${item.unit_price/100}</span>
                                                    <span style="color: var(--main-secondary-color)">$${(item.unit_price - Math.round(item.discount_amount/Math.max(item.quantity, 1)))/100}</span>/unit/${billingFrequencyTag.toLowerCase()}
                                                    <br>
                                                    Discount Code:<span style="color: var(--main-secondary-color)"> ${item.discount_name ?? 'Unknown'}</span>
                                                </div>
                                            `
                                            : html`
                                        <div>$${item.unit_price / 100}/unit/${billingFrequencyTag.toLowerCase()}</div>
                                        `
                                        }
                                    </div>
                                `)}
                            ` : ''}
                            <div class="cardButtons flexContainer" style="gap: 0.75rem; margin-top: 2rem">
                                ${subscription?.status === 'in_trial' ? '' : html`
                                    <form-button size="md" variant="outline" class="buttonOutlined" @click="${async () => await chargebeePortalClick(this.amplitude_callsite)}">
                                        View and Download Invoices
                                    </form-button>
                                `}
                                <form-button size="md" variant="outline" class="buttonOutlined" @click="${async () => await chargebeePortalClick(this.amplitude_callsite)}">
                                    Update Payment Method
                                </form-button>
                            </div>
                        ` : html`
                            <div class="flexContainer alignCenter" style="flex-direction: column">
                                <div class="cardCaption">Enterprise Billing Enabled</div>
                                <div class="cardCaption">Contact your account manager for more information</div>
                            </div>
                        `}
                    </div>
                    <p style="text-align: center;">
                        Questions? Something not right?
                        <span @click=${() => openChatWindow(this.amplitude_callsite, 'I have questions about my charges')} style="color: var(--main-secondary-color); cursor: pointer; text-decoration: underline;">
                            Contact us!
                        </span>
                    </p>
                `}
            </div>
        `;
    }

    renderSubscriptionChanger() {
        const subscription = subscriptionInfo.subscription;
        const current_tier = subscriptionInfo.subscription_tier;
        return html`
            <div class="flexContainer" style="flex-wrap: wrap; flex-direction: column; gap: 1rem">
                <div class="billingCard">
                    <div class="cardTitle">
                        ${subscription?.status === 'in_trial' || subscription?.status === 'active' ? 'Change' : 'Subscribe to'} Proxi Pro ${subscription?.status === 'in_trial' ? '(Trial)' : ''}
                    </div>
                    ${isDefined(subscriptionInfo.subscription_plan_id) && current_tier !== FeatureTierLevel.ProBase && current_tier !== FeatureTierLevel.Teams ? html`
                        <div class="flexContainer justifyBetween" style="gap: 1.5rem; text-align: center; border: 1px solid var(--main-invaid-color); border-radius: 0.5rem; padding: 1rem; margin-top: 1rem">
                            You are on a legacy plan. To make changes to your charges, you'll need to move to a new plan.
                            Reach out to support with any questions
                        </div>
                    ` : ''}
                    <cart-component id="cart-component">
                    </cart-component>
                </div>
                ${subscription?.status !== 'cancelled' && isDefined(subscription) ? html`
                    <div class="billingCard">
                        <div class="cardTitle">
                            Pause or Cancel Charges
                        </div>
                        <div class="cardButtons flexContainer" style="gap: 2rem; margin-top: 2rem;">
                            ${subscription?.status === 'in_trial' ? '' :
                                subscription?.status === 'paused' ? html`
                                    <div class="flexContainer" style="flex-direction: column; gap: 0.5rem; align-items: center">
                                        <form-button size="md" variant="outline" class="buttonOutlined" @click="${async () => await chargebeePortalClick(this.amplitude_callsite)}">
                                            Resume
                                        </form-button>
                                        Reactivate and get back to creating and editing maps.
                                    </div>
                                ` : html`
                                    <div class="flexContainer" style="flex-direction: column; gap: 0.5rem; align-items: center">
                                        <form-button size="md" variant="outline" class="buttonOutlined" @click="${async () => await chargebeePortalClick(this.amplitude_callsite)}">
                                            Pause
                                        </form-button>
                                        You can pause your account for up to 3 months, we'll keep your data safe and sound. Your maps will be read-only and static during this time.
                                    </div>
                                `}
                            <div class="flexContainer" style="flex-direction: column; gap: 0.5rem; align-items: center">
                                <form-button size="md" variant="outline" class="buttonOutlined" @click="${async () => await chargebeePortalClick(this.amplitude_callsite)}">
                                    Cancel
                                </form-button>
                                Cancel your agreement at any time. You will not be able to access maps with more than 25 points and your public maps will become static.
                            </div>
                        </div>
                    </div>
                ` : ''}
                <p style="text-align: center;">
                    Questions? Something not right?
                    <span @click=${() => openChatWindow(this.amplitude_callsite, 'I have questions about my charges')} style="color: var(--main-secondary-color); cursor: pointer; text-decoration: underline;">
                        Contact us!
                    </span>
                </p>
            </div>
        `;
    }

    static get styles() {
        return css`

            .flexContainer {
                display: flex;
            }

            @media only screen and (max-width: 550px) {
                .flexContainer {
                    flex-direction: column;
                    text-align: center;
                }
                .flexEnd {
                    justify-content: center;
                }
                #rootContainer {
                    height: auto;
                }
                .pageContainer {
                    flex-direction: column;
                    padding-left: 0;
                }
            }

            mwc-select {
                position: absolute;
            }

            mwc-icon {
                cursor: pointer;
            }

            .manageSubscription {
                color: var(--main-secondary-color);
                cursor: pointer;
            }

            .membersTable .td {
                flex: 0.75rem;
                font-size: 1rem;
                height: 2rem;
                display: flex;
                align-items: center;
            }

            .actionColumn {
                max-width: 2rem;
            }

            .billingCard  {
                flex: 1;
                font-size: 14px;
                max-width: 100%;
                padding: 1.5rem;
                border-radius: 0.5rem;
                border: 1px solid var(--theme-border-color);
                box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.05),
                    0px 2px 2px -2px rgba(31, 27, 45, 0.08);
            }

            .billingCard  .cardTitle {
                font-size: 1.25rem;
                font-weight: 700;
                margin-bottom: 0.5rem;
                text-align: left;
            }

            .billingCard {
                text-align: left;
            }

            .priceSection {
                font-size: 1.5rem;
                font-weight: 500;
            }

            .visaCardWrapper {
                border-radius: 0.5rem;
                padding: 1.25rem;
                margin-top: 1.25rem;
                gap: 1rem;
                border: 1px solid var(--theme-border-color);
                border-radius: 0.5rem;
                align-items: flex-start;
                color: var(--theme-input-color);
            }

            .alignCenter {
                align-items: center;
                justify-content: center;
            }

            .periodTag {
                border: 1px solid var(--theme-focus-border-color);
                border-radius: 5rem;
                display: inline-block;
                padding: 6px 1rem;
                margin-top: 0.5rem;
            }

            .membersCount {
                margin-top: 2rem;
                margin-bottom: 1rem;
                font-size: 1rem;
                display: flex;
                justify-content: space-between;
            }

            .visaImgWrapper {
                border: 1px solid var(--theme-border-color);
                border-radius: 0.5rem;
                padding: 0.75rem 0.5rem;
            }

            .cardButtons form-button {
                font-size: 1rem;
                font-weight: 500;
            }

            .cardButtons {
                justify-content: center;
            }

            .justifyBetween {
                justify-content: space-between;
            }

            form-button.buttonOutlined {
                color: var(--theme-light-text-color);
            }

            .visaCardWrapper form-button {
                font-size: 1rem;
                font-weight: 500;
            }

            .cardCaption {
                color: var(--theme-light-text-color);
                padding-top: 0.25rem;
            }

            vaadin-accordion {
                padding: 0.25rem 0.75rem;
                border-radius: 0.5rem;
                border: 1px solid var(--theme-border-color);
            }

            .accordionItem {
                padding-top: 0.5rem;
                padding-bottom: 0.5rem;
            }

            .accordionItem:not(:last-child) {
                border-bottom: 1px solid var(--theme-border-color);
            }

            .alignRight {
                align-items: flex-end;
                text-align: right;
                justify-content: flex-end;
            }
        `;
    }
}
